import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import CommonRepository from "../../core/repository/CommonRepository";
import ThumbnailUploadComponent from "../../component/atom/Inputs/Thumbnail";
import {
  createInitialThumbnailState,
  transformConfigurationResponse,
  buildThumbnailFormData,
  CONTENT_TYPES,
  getText,
  onToast,
} from "../../core/Constant";
import { TextInputComponent } from "../../component/atom";

const ConfigurationScreen = () => {
  const [selectedType, setSelectedType] = useState("quiz");
  const [currentThumbnails, setCurrentThumbnails] = useState(createInitialThumbnailState);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [markUpPer, setMarkUpPer] = useState("")
  const [markUpPerError, setMarkUpPerError] = useState(false);
  const [markUpPerValid, setMarkUpPerValid] = useState(false);

  const activeTypeLabel = useMemo(() => {
    const matchedType = CONTENT_TYPES.find((item) => item.id === selectedType);
    return matchedType ? matchedType.label : "Content";
  }, [selectedType]);

  const fetchConfigurationData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CommonRepository.getConfiguration();
      if (res && res.status) {
        const mappedData = transformConfigurationResponse(res);
        setCurrentThumbnails(mappedData);
        if(mappedData?.text?.value){
           setMarkUpPer(mappedData?.text?.value)
        }
      }
    } catch (err) {
      console.error("Failed to fetch configuration API:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigurationData();
  }, [fetchConfigurationData]);

  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
    setUploadedFile(null);
  };

  const handleRemoveThumbnail = async () => {
    const activeThumbnail = currentThumbnails[selectedType];
    const configurationId = activeThumbnail?.id;

    if (!configurationId) {
      console.warn("No configuration ID found for deletion");
      return;
    }

    setLoading(true);
    try {
      const response = await CommonRepository.deleteConfiguration(configurationId);
      if (response && response?.message) onToast("Configuration", response, false);

      if (response && !response.error) {
    setCurrentThumbnails((prev) => ({
      ...prev,
          [selectedType]: { url: null, id: null },
        }));
        await fetchConfigurationData();
      } else {
        console.error("Failed to delete configuration:", response?.error);
      }
    } catch (error) {
      console.error("Error deleting configuration:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = useCallback(async () => {
    if (selectedType !== "text" && !uploadedFile) {
      return;
    } 
    if(selectedType === "text" && !markUpPerValid){
     setMarkUpPerError(true)
     return
    }   
    setLoading(true);
    const configurationValue = selectedType === "text" ? markUpPer : uploadedFile;
    const formData = buildThumbnailFormData(selectedType, configurationValue);
    try {
      const res = await CommonRepository.saveConfiguration(formData);
      if (res && res?.message) onToast("Configuration", res, false);

      if (res && (res.status || !res.error)) {
        setUploadedFile(null);
        await fetchConfigurationData();
      } else {
        console.error("Failed to save configuration:", res?.message || res?.error);
      }
    } catch (error) {
      console.error("Failed to save configuration:", error);
    } finally {
      setLoading(false);
    }
  }, [uploadedFile, selectedType, fetchConfigurationData, markUpPer]);

  const currentUrl = currentThumbnails[selectedType]?.url;

  const onMarkUpPerChange = (e) => {
    const text = getText(e);
    setMarkUpPer(text);
    setMarkUpPerError(false);
    const isValid = text > 0;
    setMarkUpPerValid(isValid);
  };

  return (
    <Box className="main-screen-container">
      {/* Header */}
      <Box sx={{ mb: 3, mt: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "#1D2939", mb: 0.5 }}>
          Configuration
        </Typography>
        <Typography variant="body2" sx={{ color: "#667085" }}>
          Manage default content thumbnails for different content types. These thumbnails will be used when no custom thumbnail is uploaded.
        </Typography>
      </Box>

      {/* Select Content Type Card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          border: "1px solid #EAECF0",
          backgroundColor: "#fff",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1D2939", mb: 0.5 }}>
          Select Content Type
        </Typography>
        <Typography variant="body2" sx={{ color: "#667085", mb: 2 }}>
          Choose the content type for which you want to configure the default thumbnail.
        </Typography>

        <Grid container spacing={2}>
          {CONTENT_TYPES.map((item) => {
            const IconComponent = item.icon;
            const isSelected = selectedType === item.id;
            return (
              <Grid item xs={12} sm={6} md={2.4} key={item.id}>
                <Box
                  onClick={() => handleTypeSelect(item.id)}
                  sx={{
                    position: "relative",
                    p: 3,
                    borderRadius: "12px",
                    border: isSelected ? "2px solid #9834F0" : "1px solid #EAECF0",
                    backgroundColor: isSelected ? "#F9F5FF" : "#fff",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: "#9834F0",
                      boxShadow: "0px 4px 12px rgba(152, 52, 240, 0.08)",
                    },
                  }}
                >
                  {isSelected && (
                    <CheckCircleIcon
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "#9834F0",
                        fontSize: 20,
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "10px",
                      bgcolor: isSelected ? "rgba(152, 52, 240, 0.1)" : "#F2F4F7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1.5,
                    }}
                  >
                    <IconComponent sx={{ color: isSelected ? "#9834F0" : item.color, fontSize: 26 }} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? "#9834F0" : "#344054",
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Configure Default Thumbnail Card */}
      
      {
        selectedType !== "text" && <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          border: "1px solid #EAECF0",
          backgroundColor: "#fff",
          position: "relative",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1D2939", mb: 0.5 }}>
          Configure Default Thumbnail
        </Typography>
        <Typography variant="body2" sx={{ color: "#667085", mb: 3 }}>
          This thumbnail will be used as the default for all <strong>{activeTypeLabel}</strong> items when no custom thumbnail is available.
        </Typography>

        <Grid container spacing={3}>
          {/* Current Thumbnail Column */}
          <Grid item xs={12} md={5}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#344054", mb: 1 }}>
              Current {activeTypeLabel} Thumbnail
            </Typography>

            <Box
              sx={{
                width: "100%",
                height: 200,
                borderRadius: "12px",
                border: "1px solid #EAECF0",
                backgroundColor: "#F9FAFB",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 1,
                mb: 2,
              }}
            >
              {loading ? (
                <CircularProgress size={32} sx={{ color: "#9834F0" }} />
              ) : currentUrl ? (
                <img
                  src={currentUrl}
                  alt={`${activeTypeLabel} Thumbnail`}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <Typography variant="body2" sx={{ color: "#98A2B3" }}>
                  No default {activeTypeLabel.toLowerCase()} thumbnail set
                </Typography>
              )}
            </Box>

            <Button
              variant="outlined"
              color="error"
              fullWidth
              startIcon={<DeleteOutlineIcon />}
              onClick={handleRemoveThumbnail}
              disabled={!currentUrl || loading}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                borderColor: "#FDA29B",
                color: "#D92D20",
                "&:hover": {
                  borderColor: "#F04438",
                  backgroundColor: "#FEF3F2",
                },
              }}
            >
              Remove Thumbnail
            </Button>
          </Grid>

          {/* Upload New Thumbnail Column */}
          <Grid item xs={12} md={7}>
            <ThumbnailUploadComponent
              value={uploadedFile}
              onChange={(file) => setUploadedFile(file)}
              label="Upload New Thumbnail"
              description="PNG, JPG, JPEG up to 2MB"
              disabled={loading}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveConfiguration}
                disabled={!uploadedFile || loading}
                sx={{
                  backgroundColor: "#9834F0",
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 3,
                  py: 1,
                  boxShadow: "0 4px 12px rgba(152, 52, 240, 0.35)",
                  "&:hover": { backgroundColor: "#7a29c0" },
                }}
              >
                Save Configuration
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      }

      {
        selectedType === "text" && <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          border: "1px solid #EAECF0",
          backgroundColor: "#fff",
          position: "relative",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1D2939", mb: 0.5 }}>
          Text Markup Settings
        </Typography>
        <Typography variant="body2" sx={{ color: "#667085", mb: 3 }}>
          Set a default markup percentage for currency conversion
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12} md={5}>
                <TextInputComponent
                  label={"Markup Percentage"}
                  placeholder="Enter markup percentage"
                  type={"number"}
                  value={markUpPer}
                  isError={markUpPerError}
                  error="Please enter the valid markup percentage."
                  onchange={onMarkUpPerChange}
                  containerStyle={{
                    padding: 0,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveConfiguration}
                disabled={!markUpPerValid || loading}
                sx={{
                  backgroundColor: "#9834F0",
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 3,
                  py: 1,
                  boxShadow: "0 4px 12px rgba(152, 52, 240, 0.35)",
                  "&:hover": { backgroundColor: "#7a29c0" },
                }}
              >
                Save Configuration
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      }
      

      {/* Information Banner */}
      <Alert
        icon={<InfoOutlinedIcon sx={{ color: "#2E90FA" }} />}
        severity="info"
        sx={{
          borderRadius: "12px",
          backgroundColor: "#F0F9FF",
          border: "1px solid #B2DDFF",
          color: "#1849A9",
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Information
        </Typography>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: "0.85rem" }}>
          <li>These default thumbnails will be used across the platform when no custom thumbnail is uploaded.</li>
          <li>We recommend using high quality images with the recommended dimensions for best results.</li>
        </ul>
      </Alert>
    </Box>
  );
};

export default ConfigurationScreen;